import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { ChevronRight, Mail, ChevronDown, ChevronUp, Info, MailCheck, Check, Copy } from 'lucide-react';
import { useTranslations } from '../../i18n';
import { Car } from '../../lib/supabase';
import { useOutletContext } from 'react-router-dom';
import planLimitsData from '../../data/planLimits.json';
import { AllowedEmailsSection } from './AllowedEmailsSection';

type EmailProcessingCombinedProps = {
    car: Car;
    ownerEmail?: string;
    allowedEmails: string[];
    onAllowedEmailsChange: (emails: string[]) => void;
    isAdminView?: boolean;
    isPro?: boolean;
};

export function EmailProcessingCombined({ car, ownerEmail, allowedEmails, onAllowedEmailsChange, isAdminView, isPro: propsIsPro }: EmailProcessingCombinedProps) {
    const { t } = useTranslations();
    const navigate = useNavigate();
    const [totalEmails, setTotalEmails] = useState(0);
    const [usageStats, setUsageStats] = useState({ emails: 0, files: 0 });
    const [showLimits, setShowLimits] = useState(false);
    const [showAllowedEmails, setShowAllowedEmails] = useState(false);
    const [copied, setCopied] = useState(false);

    const outletContext = useOutletContext<any>() || {};
    const effectivePreferences = outletContext.ownerPreferences || outletContext.preferences;

    // Calculate actual limits
    const planType = effectivePreferences?.plan_type || 'free';
    const isPro = typeof propsIsPro === 'boolean' ? propsIsPro : planType !== 'free' || isAdminView;
    const planConfig = (planLimitsData as any)[planType] || (planLimitsData as any)['free'];

    // Limits
    const emailParsingLimit = (effectivePreferences?.email_parsing_limit ?? 0) > 0 ? effectivePreferences!.email_parsing_limit : planConfig.email_parsing_limit;
    const fileParsingLimit = (effectivePreferences?.file_parsing_limit ?? 0) > 0 ? effectivePreferences!.file_parsing_limit : planConfig.file_parsing_limit;

    // All cars have slugs
    const emailAddress = car.slug ? `${car.slug}-expenses@mycarcosts.com` : null;

    const fetchEmailsAndStats = async () => {
        try {
            // Fetch usage stats first
            const usageResponse = await supabase.rpc('get_user_parsing_usage', {
                p_user_id: car.user_id,
                p_is_pro: isPro
            });

            if (usageResponse.data && usageResponse.data.length > 0) {
                setUsageStats({
                    emails: parseInt(usageResponse.data[0].email_count) || 0,
                    files: parseInt(usageResponse.data[0].file_count) || 0
                });
            }

            const { count, error } = await supabase
                .from('incoming_emails')
                .select('id, created_at, from_email, status', { count: 'exact' })
                .eq('car_id', car.id)
                .order('created_at', { ascending: false })
                .limit(10); // Still fetch 10 for potential preview, but get total count

            if (!error) {
                setTotalEmails(count || 0);
            }
        } catch (err) {
            console.error('Error fetching emails/stats:', err);
        }
    };

    useEffect(() => {
        fetchEmailsAndStats();
        const interval = setInterval(fetchEmailsAndStats, 30000);
        return () => clearInterval(interval);
    }, [car.id]);

    const handleCopy = () => {
        if (!emailAddress) return;

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(emailAddress).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }).catch(err => {
                console.error('Failed to copy using navigator.clipboard:', err);
                fallbackCopy(emailAddress);
            });
        } else {
            fallbackCopy(emailAddress);
        }
    };

    const fallbackCopy = (text: string) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        // Ensure textarea is not visible but part of the DOM
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Fallback copy failed:', err);
        }
        document.body.removeChild(textArea);
    };

    const allowedCount = 1 + (allowedEmails?.length || 0);

    return (
        <div className="space-y-4">
            {/* INSTRUCTIONS & EMAIL BOX */}
            <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl p-5 shadow-sm">
                {!emailAddress ? (
                    <p className="text-sm text-gray-500 italic text-center py-4">
                        {(t.cars as any).emailAddressWillBeAvailableAfterSave || 'Email address will be available after saving the car.'}
                    </p>
                ) : (
                    <>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 font-medium leading-relaxed">
                            {t.cars.emailInstructions}
                        </p>

                        <div className="relative mb-4">
                            <button
                                type="button"
                                onClick={handleCopy}
                                className={`w-full group relative flex items-center justify-center gap-3 py-4 px-6 rounded-2xl border transition-all duration-300 font-inter text-lg font-bold shadow-inner ${copied
                                        ? 'bg-green-50 dark:bg-green-900/20 border-green-500/50 text-green-600 dark:text-green-400'
                                        : 'bg-gray-50 dark:bg-white/5 border-gray-100 dark:border-white/5 text-blue-600 dark:text-blue-400 hover:border-blue-500/50'
                                    }`}
                            >
                                <span className={`flex items-center gap-2 transition-all duration-300 ${copied ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}>
                                    <span className="break-all">{emailAddress}</span>
                                    <Copy size={18} className="flex-shrink-0 text-gray-400 group-hover:text-blue-500" />
                                </span>

                                {copied && (
                                    <div className="absolute inset-0 flex items-center justify-center gap-2 animate-in fade-in zoom-in duration-300">
                                        <Check size={20} />
                                        <span>{t.common.emailCopied}</span>
                                    </div>
                                )}
                            </button>
                        </div>
                    </>
                )}

                {/* LIMITS EXPAND/COLLAPSE */}
                <button
                    type="button"
                    onClick={() => setShowLimits(!showLimits)}
                    className="w-full flex items-center justify-between mt-4 pt-4 border-t border-gray-50 dark:border-white/5 text-xs font-bold text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors group"
                >
                    <div className="flex items-center gap-2">
                        <Info size={14} className="group-hover:scale-110 transition-transform" />
                        <span className="uppercase tracking-wider">{(t.common as any).limitsAndUsage}</span>
                    </div>
                    {showLimits ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {showLimits && (
                    <div className="mt-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                        {/* Email Limit */}
                        <div>
                            <div className="flex justify-between text-[11px] mb-1.5 uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <span className="font-bold">
                                    {isPro ? (t.common as any).monthlyEmailScans : (t.common as any).lifetimeEmailScans}
                                </span>
                                <span className="font-black text-gray-700 dark:text-gray-200">
                                    {usageStats.emails} / {emailParsingLimit}
                                </span>
                            </div>
                            <div className="w-full bg-gray-100 dark:bg-white/5 rounded-full h-1.5 overflow-hidden">
                                <div
                                    className={`h-1.5 rounded-full transition-all duration-700 ${usageStats.emails >= emailParsingLimit ? 'bg-red-500' : 'bg-blue-600'}`}
                                    style={{ width: `${Math.min(100, (usageStats.emails / emailParsingLimit) * 100)}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* File Limit */}
                        <div>
                            <div className="flex justify-between text-[11px] mb-1.5 uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <span className="font-bold">
                                    {isPro ? (t.common as any).monthlyFileScans : (t.common as any).lifetimeFileScans}
                                </span>
                                <span className="font-black text-gray-700 dark:text-gray-200">
                                    {usageStats.files} / {fileParsingLimit}
                                </span>
                            </div>
                            <div className="w-full bg-gray-100 dark:bg-white/5 rounded-full h-1.5 overflow-hidden">
                                <div
                                    className={`h-1.5 rounded-full transition-all duration-700 ${usageStats.files >= fileParsingLimit ? 'bg-red-500' : 'bg-green-500'}`}
                                    style={{ width: `${Math.min(100, (usageStats.files / fileParsingLimit) * 100)}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ALLOWED EMAILS EXPAND/COLLAPSE */}
            <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden">
                <button
                    type="button"
                    onClick={() => setShowAllowedEmails(!showAllowedEmails)}
                    className="w-full flex items-center justify-between p-5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center">
                            <MailCheck size={20} />
                        </div>
                        <div className="text-left">
                            <span className="text-base font-bold text-gray-800 dark:text-gray-100 block leading-tight">
                                {t.cars.allowedSenderEmails}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs font-bold text-gray-400 dark:text-gray-500">
                            ( {allowedCount} )
                        </span>
                        {showAllowedEmails ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                    </div>
                </button>

                {showAllowedEmails && (
                    <div className="px-5 pb-5 animate-in fade-in slide-in-from-top-2 duration-300">
                        <AllowedEmailsSection
                            ownerEmail={ownerEmail}
                            allowedEmails={allowedEmails}
                            onChange={onAllowedEmailsChange}
                            isEmbedded={true}
                            isPro={isPro}
                            isAdminView={isAdminView}
                        />
                    </div>
                )}
            </div>

            {/* VIEW EMAILS LINK */}
            <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden">
                <button
                    type="button"
                    onClick={() => navigate(`/${car.slug}/edit/emails`)}
                    className="w-full text-left p-5 text-base font-bold text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors flex items-center justify-between group"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center">
                            <Mail size={20} />
                        </div>
                        <span>{t.cars.recentEmails}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs font-bold text-gray-400 dark:text-gray-500">
                            ( {totalEmails} )
                        </span>
                        <ChevronRight className="text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" size={20} />
                    </div>
                </button>
            </div>
        </div>
    );
}
